import React, { useEffect, useState } from 'react';
import { aiApi } from '@/utils/api';
import { Plus, Trash2, X, Cpu } from 'lucide-react';
import { toast } from 'sonner';

export default function AISkillsPanel() {
  const [skills, setSkills] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', skill_type: 'Summarizer', description: '', system_prompt: '', model: 'claude-haiku-4-5', temperature: 0.3, output_format: 'markdown' });

  useEffect(() => {
    aiApi.skills().then(r => setSkills(r.data)).catch(() => {});
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const r = await aiApi.createSkill(form);
      setSkills(prev => [...prev, r.data]);
      setShowCreate(false);
      toast.success('AI skill created');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    try {
      await aiApi.deleteSkill(id);
      setSkills(prev => prev.filter(s => s.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-bold" style={{ color: 'var(--ink)' }}>AI Skills</h2>
        <button className="btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5" onClick={() => setShowCreate(true)} data-testid="add-ai-skill-button">
          <Plus size={13} /> Add Skill
        </button>
      </div>

      <div className="space-y-2">
        {skills.length === 0 && (
          <div className="card-base p-8 text-center">
            <Cpu size={32} style={{ color: 'var(--border)', margin: '0 auto 12px' }} />
            <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>No AI skills yet</p>
            <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Create reusable Claude prompts for briefs, estimates, checklists</p>
          </div>
        )}
        {skills.map(s => (
          <div key={s.id} className="card-base p-3 flex items-center justify-between" data-testid={`skill-item-${s.id}`}>
            <div>
              <p className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{s.name}</p>
              <p className="text-xs" style={{ color: 'var(--muted)' }}>{s.skill_type} · {s.model}</p>
            </div>
            <button onClick={() => handleDelete(s.id)} className="p-1.5 rounded-btn hover:bg-red-50" style={{ color: '#B34A3A' }} data-testid={`delete-skill-${s.id}`}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="card-base w-full max-w-lg p-5 animate-fade-in overflow-y-auto max-h-[90vh]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold" style={{ color: 'var(--ink)' }}>New AI Skill</h3>
              <button onClick={() => setShowCreate(false)} style={{ color: 'var(--muted)' }}><X size={16} /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Name *</label><input className="input-agency text-sm" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
                <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Skill Type</label>
                  <select className="input-agency text-sm" value={form.skill_type} onChange={e => setForm({...form, skill_type: e.target.value})}>
                    {['Summarizer','Checklist Generator','Risk Finder','Effort Estimator','Estimate Drafter','SOW Drafter','Status Report Writer','Bug Triager','Document Classifier','Chat Reply Suggester','Follow-up Drafter'].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Model</label>
                <select className="input-agency text-sm" value={form.model} onChange={e => setForm({...form, model: e.target.value})}>
                  <option value="claude-haiku-4-5">Claude Haiku (fast, cheap)</option>
                  <option value="claude-sonnet-4-5">Claude Sonnet (balanced)</option>
                  <option value="claude-opus-4-5">Claude Opus (powerful)</option>
                </select>
              </div>
              <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>System Prompt *</label><textarea className="input-agency text-sm resize-none" rows={5} required value={form.system_prompt} onChange={e => setForm({...form, system_prompt: e.target.value})} placeholder="You are an expert agency project manager. Given the following project context, ..." /></div>
              <div className="flex gap-3"><button type="button" className="flex-1 py-2 text-sm rounded-btn border font-medium" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} onClick={() => setShowCreate(false)}>Cancel</button><button type="submit" className="flex-1 btn-primary py-2 text-sm" data-testid="create-ai-skill-submit">Create Skill</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
